<script lang="ts" setup>
/*
ClockResumeWizard.vue
Wizard for resuming a paused clock with options to resume now or specify a resume time.
*/

import { ref, onMounted } from "vue";
import ClockFormResume from "@/components/Clock/ClockFormResume.vue";
import ClockFormPickTime from "@/components/Clock/ClockFormPickTime.vue";
import ClockHelper from "@/components/Clock/ClockHelper";
import Helper from "@/services/Helper";
import Api from "@/services/Api";
import Alert from "@/services/Alert";

const props = defineProps({
    task: {
        type: Object,
        required: true
    }
});

const emits = defineEmits([
    "cancel",
    "success"
]);

const step = ref(0); // 0 = loading, 1 = choose resume option, 2 = pick specific time
const ch = new ClockHelper();

// Clock data
const clockData = ref<any>({
    Request: null,
    Client: null,
    Task: null,
    Clock: null
});

/**
 * Resume the clock now (immediately)
 */
const resumeNow = async () => {
    await resumeWhen(new Date());
};

/**
 * Show the time picker to specify when to resume
 */
const specifyTime = () => {
    step.value = 2;
};

/**
 * Resume the clock at a specific time
 */
const resumeWhen = async (resumeTime: Date) => {
    console.debug("resumeWhen()", resumeTime);
    
    try {
        const clock = clockData.value.Clock;
        
        if (!clock) {
            Alert.notify("Clock data not found");
            return;
        }
        
        // Load the last pause record
        const rec = await Api.loadLastPause(clock.Id);
        
        if (!rec) {
            Alert.notify("No pause record found");
            return;
        }
        
        if (!clock.Paused) {
            Alert.notify("Clock is not paused");
            return;
        }
        
        const paused = new Date(clock.Paused);
        const diffResult = ch.calcDiff(resumeTime, paused);
        const seconds = diffResult?.seconds ?? 0;
        
        // Validate resume time is after pause time
        if (seconds < 0) {
            Alert.notify("The resume time cannot be set before the pause time.");
            return;
        }
        
        // Validate resume time is not in the future
        const now = new Date();
        if (resumeTime > now) {
            Alert.notify("The resume time cannot be in the future.");
            return;
        }
        
        // Store the resume time and duration
        rec.Resumed = resumeTime;
        rec.Diff = seconds;
        
        // Add this pause duration to total paused time
        let total = Number(clock.TimePaused || 0);
        total += seconds;
        
        clock.TimePaused = total;
        clock.Paused = null;
        
        // Save both the clock and pause record
        await Api.saveClock(clock);
        await Api.savePause(rec);
        
        emits("success");
        
    } catch (error: any) {
        console.error("Error resuming clock:", error);
        Alert.notify("Failed to resume clock: " + (error.message || "Unknown error"));
    }
};

/**
 * Cancel the resume operation
 */
const cancel = () => {
    emits("cancel");
};

// Initialize
onMounted(async () => {
    try {
        const task = props.task;
        
        // Load full task data if needed
        let fullTask = task;
        if (!task.Request || !task.Contact) {
            fullTask = await Api.loadTime(task.Id);
        }
        
        clockData.value = {
            Request: fullTask.Request || task.Request,
            Client: fullTask.Contact || task.Contact,
            Task: fullTask,
            Clock: fullTask.Clock || task.Clock
        };
        
        // Validate we have a paused clock
        if (!clockData.value.Clock?.Paused) {
            Alert.notify("This clock is not paused");
            emits("cancel");
            return;
        }
        
        step.value = 1;
    } catch (error) {
        console.error("Error loading task:", error);
        Alert.notify("Failed to load task");
        emits("cancel");
    }
});

</script>

<template>
    <div class="clock-resume-wizard">
        <!-- Loading State -->
        <div v-if="step === 0" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2 text-muted">Loading clock data...</p>
        </div>

        <!-- Step 1: Choose Resume Option -->
        <div v-if="step === 1">
            <h5 class="mb-3">Resume Clock</h5>
            <clock-form-resume
                :request="clockData.Request"
                :client="clockData.Client"
                :task="clockData.Task"
                :clock="clockData.Clock"
                @cancel="cancel"
                @now="resumeNow"
                @specify="specifyTime"
            ></clock-form-resume>
        </div>

        <!-- Step 2: Pick Specific Resume Time -->
        <div v-if="step === 2">
            <h5 class="mb-3">Specify Resume Time</h5>
            <clock-form-pick-time
                :request="clockData.Request"
                :client="clockData.Client"
                :task="clockData.Task"
                mode="resume"
                @ok="resumeWhen"
                @cancel="cancel"
                @back="step = 1"
            ></clock-form-pick-time>
        </div>
    </div>
</template>

<style scoped>
.clock-resume-wizard {
    min-height: 200px;
}
</style>
